
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useDebouncedValue } from '@/hooks/use-debounced-value';

const Index: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectAttempted = useRef<boolean>(false);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  
  // Используем дебаунс для стабилизации состояния
  const debouncedUser = useDebouncedValue(user, 300);
  const debouncedIsLoading = useDebouncedValue(isLoading, 300);

  // Улучшенная обработка перенаправлений с механизмом защиты от циклов
  useEffect(() => {
    // Выполняем перенаправление только после полной загрузки данных пользователя
    // и только если еще не выполнили перенаправление
    if (!debouncedIsLoading && debouncedUser && !redirectAttempted.current) {
      // Устанавливаем флаг, что попытка перенаправления была выполнена
      redirectAttempted.current = true;
      setIsRedirecting(true);
      
      // Добавляем существенную задержку для стабильной загрузки зависимостей и состояний
      const redirectTimeout = setTimeout(() => {
        const targetPath = debouncedUser.role === 'admin' ? '/admin' : '/courier';
        
        // Проверяем, не находимся ли мы уже на целевом пути или его подпути
        const currentPath = location.pathname;
        if (!currentPath.startsWith(targetPath)) {
          console.log(`Перенаправление с ${currentPath} на ${targetPath}`);
          // Используем navigate вместо <Navigate> для программного перенаправления
          // с опцией replace, чтобы заменить текущий маршрут в истории
          navigate(targetPath, { replace: true });
        } else {
          console.log(`Перенаправление не требуется: уже на пути ${currentPath}`);
          setIsRedirecting(false);
        }
      }, 500); // Увеличиваем задержку до 500мс для гарантии стабильности
      
      return () => {
        clearTimeout(redirectTimeout);
        setIsRedirecting(false);
      };
    }
  }, [debouncedUser, debouncedIsLoading, navigate, location.pathname]);

  // Отладочная информация
  console.log(`Index: путь=${location.pathname}, пользователь=${!!debouncedUser}, загрузка=${debouncedIsLoading}, редирект=${isRedirecting}`);

  // Показываем заглушку во время загрузки
  if (debouncedIsLoading || isRedirecting) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mr-2"></div>
        <div className="text-primary">Загрузка интерфейса...</div>
      </div>
    );
  }

  // Если пользователь не аутентифицирован, перенаправляем на страницу входа
  if (!debouncedUser) {
    return <Navigate to="/login" replace />;
  }

  // Проверяем текущий путь и определяем, нужно ли выполнять перенаправление
  const currentPath = location.pathname;
  const targetPath = debouncedUser.role === 'admin' ? '/admin' : '/courier';
  
  // Если уже находимся на нужном пути или на корневом пути (/)
  if (currentPath.startsWith(targetPath) || currentPath === '/') {
    // Возвращаем перенаправление на целевой путь, если находимся на корне
    if (currentPath === '/') {
      return <Navigate to={targetPath} replace />;
    }
    
    // Заглушка на время выполнения программного перенаправления
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-primary">Загрузка интерфейса...</div>
      </div>
    );
  }
  
  // В других случаях перенаправляем на целевой путь
  return <Navigate to={targetPath} replace />;
};

export default Index;
